/**
 * Card on the Lab Detail screen (`/admin/lab/:id`) that lets admin / lab
 * tech turn the published Aware test into a draft Pristine Water Systems
 * proposal. Always creates a DRAFT — never auto-sends.
 *
 * After a successful POST it redirects to /admin/proposals?focus={id} so
 * the proposal detail drawer auto-opens for review/edit.
 */
import { useState } from "react";
import { FilePlus, Lock, AlertCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export function CreateProposalFromTestCard({ test, isAdmin, navigate }) {
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const aiRequiresStephen = !!test?.ai_summary?.requires_stephen_review;

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/proposals/from-test/${test.id}`);
      setLastResult(data);
      const matched = data.matched?.length ?? 0;
      const skipped = data.skipped?.length ?? 0;
      toast.success(`Draft estimate ${data.reference} created (${matched} item${matched === 1 ? "" : "s"} matched${skipped ? `, ${skipped} skipped` : ""})`);
      // Hand-off to /admin/proposals with the new draft pre-selected
      navigate(`/admin/proposals?focus=${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not create estimate");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="lab-create-proposal-card" className="rounded-3xl bg-gradient-to-br from-slate-900 to-cyan-900 text-white p-6">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-2xl bg-white/10 flex items-center justify-center flex-shrink-0">
          <FilePlus className="w-5 h-5 text-cyan-200" />
        </div>
        <div>
          <h3 className="font-display text-lg font-semibold text-white">Create PWS estimate from this test</h3>
          <p className="text-sm text-cyan-100/80 mt-1 leading-relaxed">
            Turn this report into a Pristine Water Systems draft estimate — customer details, the Aware reference and any matching recommended products are pre-filled. The draft is <strong className="text-white">not sent</strong> until you click Send.
          </p>
        </div>
      </div>

      {aiRequiresStephen && (
        <div data-testid="lab-create-proposal-review-warning" className="bg-amber-500/10 border border-amber-400/30 rounded-xl p-3.5 flex items-start gap-2.5 text-sm text-amber-100 mb-4">
          <Lock className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <strong>Stephen/Admin review required.</strong> A draft estimate can be created now, but it cannot be sent to the customer until the review on the linked report is resolved.
          </div>
        </div>
      )}

      <Button
        data-testid="lab-create-proposal-btn"
        onClick={create}
        disabled={busy}
        className="rounded-full font-semibold bg-cyan-500 hover:bg-cyan-400 text-slate-900"
      >
        {busy
          ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating draft…</>
          : <><FilePlus className="w-4 h-4 mr-1.5" /> Create estimate from test</>}
      </Button>

      {lastResult?.skipped?.length > 0 && (
        <div data-testid="lab-create-proposal-skipped" className="mt-4 bg-white/5 border border-white/10 rounded-xl p-3.5">
          <div className="flex items-start gap-2 text-amber-200 text-xs font-semibold uppercase tracking-wider mb-1.5">
            <AlertCircle className="w-3.5 h-3.5" />
            Recommendations skipped — no matching catalog item
          </div>
          <ul className="text-xs text-slate-300 space-y-1">
            {lastResult.skipped.map((s, i) => (
              <li key={i} className="font-mono">
                <span className="text-slate-200">{s.key}</span>
                {s.keywords?.length ? <span className="text-slate-400"> · {s.keywords.join(", ")}</span> : null}
                <span className="text-slate-500"> — {s.reason}</span>
              </li>
            ))}
          </ul>
          <p className="text-[11px] text-slate-400 mt-2 leading-relaxed">
            Create a matching active Product &amp; Service to have these recommendations auto-imported next time.
          </p>
        </div>
      )}
    </section>
  );
}
